import {Component, computed, ElementRef, input, output, signal, viewChild} from '@angular/core';
import {OpenTable} from '../shared/open-tables.model';
import {cmpName, fmtClock, fmtDur, fmtEur} from '../shared/idle-warning-levels';

/**
 * Disambiguates the `id`s of two lists rendered into the same document.
 *
 * A card's menu id has to be stable and unique for `aria-controls` to mean anything; the reference id alone is
 * unique per list but not per document, and nothing stops a host from rendering this component twice.
 */
let instanceCounter = 0;

/** How the cards are ordered. `wait` (longest idle first) is the default — the thing a service is scanning for. */
export type OpenTablesSort = 'wait' | 'name';

/**
 * How many item lines a collapsed card shows before it folds the rest behind "+ N weitere Artikel"
 * (the handover's §4).
 */
export const COLLAPSED_ITEM_COUNT = 3;

/**
 * Breathing room, in pixels, left between a revealed card and the edge of the scroll container
 * (see {@link OpenTablesListComponent.revealCard}).
 */
export const REVEAL_PADDING_PX = 14;

/**
 * The card list of the Offene-Tische overview — the same component in both layouts: a single scrolling column
 * beside the map (`split`), and a responsive card **grid** when the list has the screen to itself.
 *
 * **Every value here is the handover's** (`Offene Tische.dc.html`): paddings, radii, font sizes, the accent
 * `#C50E6C`, the 1.5 px selection ring. Two deliberate deviations, both documented at their use site:
 *  * the **waiter avatar is omitted** — nothing in `Order`/`OrderStatus` attributes a booking to an employee, so
 *    there is no data to put in it (#205 decision 3);
 *  * three neutral greys are **darkened by a hair** (`#8a8a8e`/`#a0a0a4` → `#6b6b70`, `#158a52` → `#0f7040`)
 *    because the originals sit at 3.4:1 / 2.3:1 / 4.0:1 on their own backgrounds and fail WCAG AA, which the
 *    mandatory Storybook a11y pass would flag. The shift is imperceptible side by side.
 *
 * ## Why a card is a wrapper + a button, and not just a button (#204)
 *
 * Each card carries a `⋯` menu (copy the id; change the spending limit). A `<button>` **may not contain another
 * interactive control** — the parser does not even keep the nesting, the inner control is unreachable by
 * keyboard, and the card's own `aria-pressed` stops describing anything coherent. Three ways out were weighed:
 *
 *  * *demote the card to a `<div role="button">`* — costs the native pressed/focus/space-and-enter behaviour and
 *    hand-rolls it back, worse in every respect;
 *  * *move the actions off the card* (a toolbar over the list, acting on the selection) — a different product,
 *    and not the placement that was chosen;
 *  * **keep the card a real `<button>` and put the kebab beside it**, with a `position: relative` wrapper laying
 *    the kebab over the card's top-right corner. This is what ships.
 *
 * The result keeps everything the card had: **one** keyboard stop selects it, `aria-pressed` still describes it,
 * and the ring/`:active` styling is untouched. The kebab is a second, separately reachable stop with its own
 * accessible name, and because it is a *sibling* rather than a descendant, its press never has to be stopped
 * from reaching the card. The only cost is a 26 px reserved lane in `.otl-card-head` — which is also why
 * `LIST_MIN_WIDTH_PX` moved from 360 to 390 (see its own docs).
 */
@Component({
  selector: 'app-open-tables-list',
  standalone: true,
  imports: [],
  // Any press that lands outside an open menu closes it. `pointerdown` rather than `click` so the menu is gone
  // before the press resolves, and so a press on the kebab itself (which fires pointerdown first, then click)
  // is recognised as being inside and left to the toggle.
  host: {'(document:pointerdown)': 'onDocumentPointerDown($event)'},
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 0;
      flex: 1;
      /* The handover's document sets NO line-height, so every card is sized by the font's own normal leading.
         Tailwind's preflight puts 1.5 on <html>, which inflates a card by ~14 px and the whole list noticeably.
         Same reset the Tischplan editor's shell applies for the same reason. */
      line-height: normal;
    }

    /* The app's global \`svg { fill: currentColor }\` beats the \`fill="none"\` presentation attribute these outline
       icons rely on, flooding the clock and chevron with their stroke colour. Restore the authored intent. */
    svg[fill='none'] {
      fill: none;
    }

    .otl-head {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 13px 16px 12px;
      border-bottom: 1px solid #f1efec;
    }

    .otl-head-label {
      font-size: 13px;
      font-weight: 600;
      /* The handover's #a0a0a4 fails contrast on both list backgrounds — see the class docs. */
      color: #6b6b70;
      letter-spacing: .06em;
      text-transform: uppercase;
    }

    .otl-chips {
      display: flex;
      gap: 4px;
      background: #f0efed;
      border-radius: 10px;
      padding: 4px;
    }

    .otl-chip {
      display: inline-flex;
      align-items: center;
      padding: 8px 16px;
      border: 0;
      border-radius: 8px;
      font: inherit;
      font-size: 13.5px;
      font-weight: 600;
      background: transparent;
      color: #6b6b70;
      cursor: pointer;
      transition: background .12s, color .12s, box-shadow .12s, transform .06s;
    }

    .otl-chip:active {
      transform: translateY(1px);
    }

    .otl-chip[aria-pressed='true'] {
      background: #fff;
      color: #c50e6c;
      box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
    }

    .otl-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }

    .otl-scroll::-webkit-scrollbar {
      width: 9px;
    }

    .otl-scroll::-webkit-scrollbar-thumb {
      background: #d3d0cb;
      border-radius: 999px;
      border: 2px solid transparent;
      background-clip: padding-box;
    }

    /* Split: one column of cards. */
    .otl-inner-column {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    /* Full width: the handover's responsive grid, centred and capped so lines never run absurdly long. */
    .otl-inner-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 14px;
      max-width: 1200px;
      margin: 0 auto;
    }

    /* The card's positioning context, and the ONLY reason it exists (#204).
       The card itself has to stay a <button> — that is what makes it one keyboard stop with a real
       \`aria-pressed\` state — and HTML forbids a nested interactive control inside a button. The kebab is
       therefore a SIBLING of the card button, laid over its top-right corner by this wrapper, rather than a
       descendant of it. See the class docs for the full rationale. */
    .otl-card-host {
      position: relative;
      display: block;
    }

    .otl-card {
      display: block;
      width: 100%;
      text-align: left;
      font: inherit;
      color: inherit;
      background: #fff;
      border-radius: 16px;
      padding: 15px 16px;
      border: 1.5px solid #e6e4e0;
      box-shadow: 0 1px 2px rgba(0, 0, 0, .03);
      cursor: pointer;
      transition: border-color .12s, box-shadow .12s, transform .06s;
    }

    .otl-card:active {
      transform: translateY(1px);
    }

    .otl-card[aria-pressed='true'] {
      border-color: #c50e6c;
      box-shadow: 0 0 0 3px rgba(197, 14, 108, .14), 0 10px 26px -14px rgba(0, 0, 0, .3);
    }

    /* Centred, not flex-start. With the "Tisch NNN" label gone the head is a 27 px numeral beside a 17 px pill,
       and top-aligning (or baseline-aligning) those two leaves the pill visibly high/low against a numeral more
       than half as tall again. Centring is measured against the render, not assumed: the line-height of 1 on
       the numeral makes its box exactly its em box, so the box centre and the digits' optical centre coincide
       to within a pixel — see .otl-number below. */
    .otl-card-head {
      display: flex;
      align-items: center;
      gap: 12px;
      /* The kebab's lane. It is drawn over the card, so the head — the only row that reaches the top-right
         corner — reserves the width instead of letting the timer pill slide underneath it.

         26 px, not a round 30: the kebab is 28 px wide at right: 12px, i.e. it ends 40 px from the card's
         border, and the card's own 16 px padding already covers the first 16 of those. 24 would do; the extra
         2 keeps the pill from touching it.

         Applied unconditionally because the kebab is unconditional — "ID kopieren" is available to every role,
         so the head geometry never depends on who is looking at the card. It is also why
         LIST_MIN_WIDTH_PX had to move: see its docs. */
      padding-right: 26px;
    }

    .otl-number {
      flex: 0 0 auto;
      font-size: 27px;
      font-weight: 700;
      /* Load-bearing for the centring above: without it the line box picks up the inherited leading and the
         glyphs sit off-centre inside a taller box, which drags the pill out of alignment with what you see. */
      line-height: 1;
    }

    /* Everything inside the card is a <span>: a <button> may not contain block-level content, and the card has to
       BE the button so it is one keyboard stop with a real pressed state. Layout is restored explicitly. */
    .otl-titles {
      display: block;
      flex: 1;
      min-width: 0;
    }

    .otl-title {
      font-size: 14.5px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 7px;
      flex-wrap: wrap;
      /* Anchors the absolutely-positioned .otl-sr span, so it is clipped here rather than against whatever
         happens to be the nearest positioned ancestor further up. */
      position: relative;
    }

    /* Visually hidden, still announced. The card's big numeral is the ONLY visible carrier of the table name
       (the redundant "Tisch 17" label beside it was removed), so the word has to survive here or a screen
       reader would read a bare digit with no idea what it names. Deliberately not Tailwind's .sr-only: this
       component uses none of the utility scale, and a local rule cannot be purged out from under it. */
    .otl-sr {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    /* Shared shape of the two placement badges. Only the palette and the border differ between them — the words
       do the actual distinguishing, which is what keeps them apart for a colour-blind or screen-reader user. */
    .otl-badge {
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: .05em;
      text-transform: uppercase;
      padding: 2px 7px;
      border-radius: 999px;
      border: 1px solid transparent;
    }

    /* A genuine walk-in: unremarkable, because it is a normal thing for a floor to have. */
    .otl-badge-adhoc {
      background: #f0efed;
      color: #6b6b70;
    }

    /* A table that no longer exists. Warmer, and dashed rather than solid — an outline a reader can tell from the
       ad-hoc pill at a glance without reading a colour. #7a4b1e on #f4ece3 is 6.3:1, well clear of AA. */
    .otl-badge-removed {
      background: #f4ece3;
      color: #7a4b1e;
      border-color: #c9a882;
      border-style: dashed;
    }

    .otl-meta {
      display: block;
      font-size: 12.5px;
      color: #6b6b70;
      margin-top: 1px;
    }

    .otl-timer {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      flex: 0 0 auto;
      padding: 6px 11px;
      border-radius: 999px;
      font-size: 13px;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }

    .otl-items {
      margin-top: 12px;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    /* One item AND its modification sub-lines (#423) — the unit the .otl-items gap now separates, so a swap line
       hugs the item it qualifies instead of floating half-way to the next one. Purely a grouping box: a card
       whose lines have no modifications renders exactly what it rendered before. */
    .otl-item-block {
      display: block;
    }

    .otl-item {
      display: flex;
      align-items: baseline;
      gap: 10px;
      font-size: 13.5px;
    }

    .otl-item-qty {
      flex: 0 0 auto;
      font-weight: 600;
      color: #c50e6c;
      min-width: 26px;
    }

    .otl-item-name {
      flex: 1;
      min-width: 0;
      color: #3d3d40;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .otl-item-price {
      flex: 0 0 auto;
      color: #6b6b70;
      font-variant-numeric: tabular-nums;
    }

    /* ---- interchanged components (#423) --------------------------------------------------------------- */

    /* The line's swaps/removals/adds, as the server already rendered them. Deliberately the SAME class names the
       orders table, the employee-sales popup and the event-details popup use (.modification-lines /
       .modification-line / .modification-connector, defined globally in styles.scss) so the four surfaces cannot
       drift apart — the whole point of #423.

       What is local, and why: everything inside the card is a <span> (a <button> may not contain block-level
       content, see .otl-titles), so the block layout the other surfaces get free from their <div>s has to be
       restored here. The left indent lines the sub-lines up under the item NAME rather than under its quantity:
       26 px of .otl-item-qty plus the row's 10 px gap. */
    .otl-item-mods {
      display: block;
      margin-left: 36px;
      /* A hair under the 13.5 px item row, and in the same grey as the meta line — the modification is a
         qualifier on the item above it, not a line of its own to be scanned. */
      font-size: 12.5px;
      color: #6b6b70;
    }

    .otl-item-mods .modification-line {
      display: block;
      /* The card's items are one-line-ellipsised (.otl-item-name); a modification is short but not bounded, so it
         wraps rather than being silently cut in half. */
      overflow-wrap: anywhere;
    }

    /* Stands in for the item block when every line has been settled. Uses the same "nothing is wrong here"
       green as the fresh timer (#0f7040, the AA-safe step down from the handover's #158a52) so the state reads
       as resolved rather than as an error. */
    .otl-settled {
      margin-top: 12px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 12.5px;
      font-weight: 600;
      color: #0f7040;
    }

    .otl-more {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 12.5px;
      color: #c50e6c;
      font-weight: 600;
    }

    .otl-foot {
      margin-top: 13px;
      padding-top: 12px;
      border-top: 1px solid #f1efec;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    .otl-last {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      font-size: 12.5px;
      color: #6b6b70;
    }

    .otl-total {
      font-size: 18px;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
    }

    .otl-empty {
      padding: 48px 24px;
      text-align: center;
      font-size: 14px;
      color: #6b6b70;
    }

    /* ---- open state + spending limit (#204) ---------------------------------------------------------- */

    /* Sits between the item block and the footer: the two facts that describe the OPEN reference rather than
       what has been booked on it. Wraps on a narrow rail instead of squeezing the two clauses onto one line. */
    .otl-open {
      margin-top: 10px;
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 4px 10px;
      font-size: 12.5px;
      color: #6b6b70;
    }

    .otl-open-limit {
      font-variant-numeric: tabular-nums;
    }

    /* Already booked past the limit. A real state — the limit gates the register's NEXT booking, it does not
       unbook anything — so it is called out rather than shown as an ordinary number. #c0392b on white is 5.0:1,
       the same Alert red the escalation levels use. */
    .otl-open-over {
      color: #c0392b;
      font-weight: 600;
    }

    /* ---- per-card actions (#204) --------------------------------------------------------------------- */

    /* Laid over the card button's top-right corner (see .otl-card-host). z-index keeps it above the card so the
       press lands here and not on the card underneath — it is a sibling, so there is no propagation to stop. */
    .otl-kebab {
      position: absolute;
      top: 13px;
      right: 12px;
      z-index: 2;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 0;
      border-radius: 8px;
      background: transparent;
      color: #6b6b70;
      cursor: pointer;
      transition: background .12s, color .12s;
    }

    .otl-kebab:hover,
    .otl-kebab[aria-expanded='true'] {
      background: #f0efed;
      color: #c50e6c;
    }

    .otl-menu {
      position: absolute;
      top: 45px;
      right: 12px;
      z-index: 3;
      min-width: 186px;
      display: flex;
      flex-direction: column;
      padding: 5px;
      background: #fff;
      border: 1px solid #e6e4e0;
      border-radius: 12px;
      box-shadow: 0 14px 32px -14px rgba(0, 0, 0, .4);
    }

    .otl-menu-item {
      border: 0;
      background: transparent;
      font: inherit;
      font-size: 13.5px;
      text-align: left;
      color: #1c1c1e;
      padding: 8px 10px;
      border-radius: 8px;
      cursor: pointer;
    }

    .otl-menu-item:hover,
    .otl-menu-item:focus-visible {
      background: #f5f4f2;
    }
  `],
  template: `
    <div class="otl-head">
      <span class="otl-head-label" id="otl-sort-label">Sortieren</span>
      <div class="otl-chips" role="group" aria-labelledby="otl-sort-label">
        <button type="button" class="otl-chip" [attr.aria-pressed]="sort() === 'wait'"
                (click)="sortChanged.emit('wait')">Wartezeit
        </button>
        <button type="button" class="otl-chip" [attr.aria-pressed]="sort() === 'name'"
                (click)="sortChanged.emit('name')">Name
        </button>
      </div>
    </div>

    <div #scroller class="otl-scroll" [style.padding]="layout() === 'grid' ? '20px 24px 40px' : '14px 16px 24px'">
      @if (sorted().length === 0) {
        <p class="otl-empty">{{ emptyText() }}</p>
      } @else {
        <div [class]="layout() === 'grid' ? 'otl-inner-grid' : 'otl-inner-column'">
          @for (row of sorted(); track row.id) {
            <!--
              The card is a wrapper + a full-bleed button, NOT a button with things inside it (#204). The kebab
              has to be a real control, and HTML forbids one inside a <button> — so it is a SIBLING laid over the
              card's corner. See the class docs for what that buys and what it costs.
            -->
            <div class="otl-card-host" (keydown.escape)="closeMenu(row.id)">
              <button type="button" class="otl-card" [attr.data-card-id]="row.id"
                      [attr.aria-pressed]="row.id === selectedId()"
                      (click)="selected.emit(row.id)">
                <span class="otl-card-head">
                  <!--
                    The numeral is the table's name, so it is hidden from assistive tech and the same information
                    is announced ONCE, in words, from the .otl-sr span below. Without the aria-hidden a screen
                    reader would read "17 Tisch 17".
                  -->
                  <span class="otl-number" aria-hidden="true"
                        [style.color]="row.id === selectedId() ? accent : row.level.numberColor">{{ row.number }}</span>
                  <span class="otl-titles">
                    <span class="otl-title">
                      <!--
                        "Tisch 17" used to be spelled out here beside the numeral, which said the number twice on
                        every card. Only the numeral is drawn now; the words stay for screen readers.
                      -->
                      <span class="otl-sr">Tisch {{ row.number }}</span>
                      <!--
                        The ONE affordance kept for "not placed on the map" (see metaOf): the row exists only in
                        the list, and a service scanning the two halves needs to know why this table has no marker.
                        It stays because it is a badge, not a caption — a few characters that ride the title line
                        and cost no row of their own.

                        The two states are told apart by their WORDS, not by their colours, and each carries a
                        visually hidden clause so a screen reader gets the full sentence rather than a bare label
                        (the title attribute is a hover affordance; assistive tech treats it inconsistently and a
                        touch user never sees it at all).
                      -->
                      @switch (row.placement) {
                        @case ('unplaced') {
                          <span class="otl-badge otl-badge-adhoc" title="Nicht auf der Karte">ad hoc<span
                            class="otl-sr"> — nicht auf der Karte</span></span>
                        }
                        @case ('removed') {
                          <!-- The reported bug's row: the table is not temporary, it is gone. Saying "ad hoc" here
                               told the user their table was a walk-in, which it never was. -->
                          <span class="otl-badge otl-badge-removed" title="Dieser Tisch existiert nicht mehr">entfernt<span
                            class="otl-sr"> — dieser Tisch existiert nicht mehr</span></span>
                        }
                      }
                    </span>
                    @if (metaOf(row); as meta) {
                      <span class="otl-meta">{{ meta }}</span>
                    }
                  </span>
                  <span class="otl-timer"
                        [style.background]="row.level.timerSurface" [style.color]="row.level.timerColor">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="9"/>
                      <path d="M12 7v5l3 2"/>
                    </svg>{{ fmtDur(row.idleMinutes) }}
                  </span>
                </span>

                @if (row.items.length > 0) {
                  <span class="otl-items">
                    @for (item of visibleItems(row); track $index) {
                      <span class="otl-item-block">
                        <span class="otl-item">
                          <span class="otl-item-qty">{{ item.qty }}</span>
                          <span class="otl-item-name">{{ item.name }}</span>
                          <span class="otl-item-price">{{ fmtEur(item.total) }}</span>
                        </span>
                        <!--
                          #423: the line's interchanged components — the swap that made this "Gin Tonic" a
                          different drink from the "Gin Tonic" on the next card. Rendered with the SAME classes as
                          the orders table and the two detail popups (see .otl-item-mods) so the surfaces cannot
                          drift; the connector glyph gets its own monospace column from the global
                          .modification-connector rule, exactly like the register's basket.

                          No @if guard on the array itself: the store guarantees it is an array (see
                          OpenTableItem.modificationLines), and an empty one renders no wrapper at all.
                        -->
                        @if (item.modificationLines.length > 0) {
                          <span class="otl-item-mods modification-lines">
                            @for (line of item.modificationLines; track $index) {
                              <span class="modification-line">
                                <span class="modification-connector">{{ line.connector }}</span>{{ line.text }}
                              </span>
                            }
                          </span>
                        }
                      </span>
                    }
                    @if (hiddenQuantity(row); as hidden) {
                      <span class="otl-more">+ {{ hidden }} weitere Artikel
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                          <path d="M6 9l6 6 6-6"/>
                        </svg>
                      </span>
                    }
                  </span>
                } @else if (row.settledOnly) {
                  <!--
                    Everything on this table has been paid, but the table is still open. A blank item block would
                    read as "loading" or "broken"; saying it outright turns the card into the actionable thing it
                    is — a table that can be closed.
                  -->
                  <span class="otl-settled">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M4 12.5l5 5L20 7"/>
                    </svg>Alle Artikel bezahlt
                  </span>
                }

                <!--
                  The open reference itself (#204), as opposed to what has been booked on it: since when the table
                  stands open, and how much of its spending limit is left. Rendered whenever there is something to
                  say, and additionally on the SELECTED card even when there is not — an admin about to set a limit
                  from the kebab needs to see that there is none, and that is exactly the card they have open.

                  Never "geöffnet von": openedByEmployeeId rides the contract but no client populates it, so the
                  line would print a raw guid at the guests' table. See OpenTable.openedUtc.
                -->
                @if (openState(row); as state) {
                  <span class="otl-open">
                    @if (state.openedAt) {
                      <span>geöffnet {{ state.openedAt }}</span>
                    }
                    <span class="otl-open-limit" [class.otl-open-over]="state.over">{{ state.limit }}</span>
                  </span>
                }

                <!-- The handover puts a waiter avatar left of this line; nothing in the domain attributes a booking
                     to an employee (#205 decision 3), so the line simply starts flush instead of leaving a hole. -->
                <span class="otl-foot">
                  <span class="otl-last">letzte Buchung {{ fmtClock(row.lastActivity) }}</span>
                  <span class="otl-total">{{ fmtEur(row.total) }}</span>
                </span>
              </button>

              <!--
                Deliberately OUTSIDE the card button. Its own tab stop, its own accessible name (an icon-only
                control on a list of nine identical cards has to say WHICH table it acts on), and no press of it
                ever reaches the card — a sibling has nothing to propagate to.
              -->
              <button type="button" class="otl-kebab" [attr.data-kebab-id]="row.id"
                      aria-haspopup="menu" [attr.aria-expanded]="menuFor() === row.id"
                      [attr.aria-controls]="menuId(row)"
                      [attr.aria-label]="'Aktionen für Tisch ' + row.number"
                      (click)="toggleMenu(row.id)">
                <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    d="M3 10a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0ZM8.5 10a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0ZM15.5 8.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"
                    fill="currentColor"/>
                </svg>
              </button>

              @if (menuFor() === row.id) {
                <div class="otl-menu" role="menu" [id]="menuId(row)"
                     [attr.aria-label]="'Aktionen für Tisch ' + row.number">
                  <button type="button" role="menuitem" class="otl-menu-item"
                          (click)="raiseCopy(row)">ID kopieren
                  </button>
                  <!--
                    The web half of the deliberately asymmetric #204 lifecycle gating: a limit CHANGE only, for
                    ShardAdmin/CompanyOwner/PlatformOwner. There is no close action here — closing a table is
                    register-only floor work. Operator reaches this screen too (#205) and must NOT see this: it is
                    a read-only baseline role, and the host decides that (see the canChangeLimit input).
                  -->
                  @if (canChangeLimit()) {
                    <button type="button" role="menuitem" class="otl-menu-item"
                            (click)="raiseLimit(row)">Ausgabenlimit ändern
                    </button>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  `
})
export class OpenTablesListComponent {
  readonly tables = input<readonly OpenTable[]>([]);
  readonly selectedId = input<string | null>(null);
  readonly sort = input<OpenTablesSort>('wait');
  /** `column` beside the map, `grid` when the list owns the width. */
  readonly layout = input<'column' | 'grid'>('column');
  /** What to show when there is nothing open — the host phrases it, because it knows why (loading vs. empty). */
  readonly emptyText = input('Zurzeit sind keine Tische offen.');
  /**
   * Whether the "Ausgabenlimit ändern" item is offered (#204).
   *
   * The **host** answers this, not the list: the list is presentational and has no business inspecting a session.
   * The rule it stands for is `ShardAdmin`/`CompanyOwner`/`PlatformOwner` — and pointedly **not** `Operator`,
   * the read-only baseline role that reaches this screen since #205.
   */
  readonly canChangeLimit = input(false);

  readonly sortChanged = output<OpenTablesSort>();
  readonly selected = output<string>();
  /** "ID kopieren" was chosen. The clipboard write and its toast belong to the host. */
  readonly copyRequested = output<OpenTable>();
  /** "Ausgabenlimit ändern" was chosen. The dialog and the save belong to the host. */
  readonly limitRequested = output<OpenTable>();

  private readonly scroller = viewChild.required<ElementRef<HTMLElement>>('scroller');

  /** The card whose kebab menu is open, or `null`. At most one is open at a time, by construction. */
  protected readonly menuFor = signal<string | null>(null);

  private readonly uid = ++instanceCounter;

  protected readonly accent = '#C50E6C';
  protected readonly fmtEur = fmtEur;
  protected readonly fmtDur = fmtDur;
  protected readonly fmtClock = fmtClock;

  /**
   * The cards in render order.
   *
   * `wait` sorts longest-idle first; a row that has never been booked on (`idleMinutes === null`) sorts to the
   * bottom rather than to the top, which is what a `null`-as-0 comparison would silently do.
   */
  protected readonly sorted = computed<OpenTable[]>(() => {
    const rows = [...this.tables()];
    if (this.sort() === 'name') return rows.sort((a, b) => cmpName(a.number, b.number));
    return rows.sort((a, b) => (b.idleMinutes ?? -1) - (a.idleMinutes ?? -1));
  });

  /**
   * `Innen · 4 Plätze`, degrading gracefully when the row has no area and/or no seat count.
   *
   * With nothing to say, only a **placed** row falls back to "Ohne Bereich": there the absence is a real, fixable
   * gap in the floor plan. The other two placements return the empty string and the template renders no meta line
   * at all —
   *  * `unplaced` never had a position to be missing. This used to read "Ohne Grundriss-Position", a deliberate,
   *    user-approved removal: most rows on a real floor are ad hoc, so the caption appeared on nearly every card,
   *    said the same thing every time, and was long enough to wrap mid-phrase in the split view — three lines of
   *    noise for one bit that the badge on the title already carries;
   *  * `removed` must not claim "Ohne Bereich" either. Its table is gone, so it has no area in the sense the
   *    phrase implies — the caption would read as a gap someone could go and fill in, which is precisely wrong.
   */
  protected metaOf(row: OpenTable): string {
    const parts: string[] = [];
    if (row.areaName) parts.push(row.areaName);
    if (row.seats !== null) parts.push(`${row.seats} Plätze`);
    if (parts.length === 0) return row.placement === 'placed' ? 'Ohne Bereich' : '';
    return parts.join(' · ');
  }

  /**
   * The open reference's own state, as the two clauses the card draws — or `null` when there is nothing to draw.
   *
   * The visibility rule is deliberately not "always": a row that was never opened by hand and carries no limit
   * has nothing to say, and a line reading "kein Ausgabenlimit" on all nine cards is noise on a screen whose job
   * is to make the *unusual* table stand out. The **selected** card is the exception — it is the one an admin is
   * about to act on from the kebab, so it states the absence explicitly rather than leaving them to infer it.
   *
   * @returns `openedAt` as a local `HH:MM` clock (or `null`), the limit clause, and whether the table has
   *   already been booked past its limit.
   */
  protected openState(row: OpenTable): {openedAt: string | null; limit: string; over: boolean} | null {
    const hasLimit = row.spendingLimit !== null;
    if (row.openedUtc === null && !hasLimit && row.id !== this.selectedId()) return null;
    const headroom = row.headroom ?? 0;
    return {
      openedAt: row.openedUtc === null ? null : fmtClock(row.openedUtc),
      limit: hasLimit
        ? `Limit ${fmtEur(row.spendingLimit!)} · Rest ${fmtEur(headroom)}`
        : 'kein Ausgabenlimit',
      over: hasLimit && headroom < 0
    };
  }

  /** The `id` of a card's menu, unique per document — see {@link instanceCounter}. */
  protected menuId(row: OpenTable): string {
    return `otl-menu-${this.uid}-${row.id}`;
  }

  /** Opens this card's kebab menu, or closes it if it was already the open one. */
  protected toggleMenu(id: string): void {
    this.menuFor.update(current => current === id ? null : id);
  }

  /**
   * Closes the menu and hands focus back to the kebab that opened it — the Escape contract for a menu button.
   * Dropping the focus on the closed menu's dead element would strand a keyboard user at the top of the page.
   */
  protected closeMenu(id: string): void {
    if (this.menuFor() !== id) return;
    this.menuFor.set(null);
    this.scroller().nativeElement
      .querySelector<HTMLElement>(`[data-kebab-id="${CSS.escape(id)}"]`)?.focus();
  }

  /** A press anywhere outside the open menu (and outside its own kebab) dismisses it. */
  protected onDocumentPointerDown(event: Event): void {
    if (this.menuFor() === null) return;
    // `Element` guard, not a cast: an event's target can be the document, the window or a text node, none of
    // which carry `closest` — and a TypeError raised in a document-level listener takes the whole screen down.
    const target = event.target;
    if (target instanceof Element && target.closest('.otl-menu, .otl-kebab')) return;
    this.menuFor.set(null);
  }

  protected raiseCopy(row: OpenTable): void {
    this.menuFor.set(null);
    this.copyRequested.emit(row);
  }

  protected raiseLimit(row: OpenTable): void {
    this.menuFor.set(null);
    this.limitRequested.emit(row);
  }

  /** All items while selected, otherwise the first {@link COLLAPSED_ITEM_COUNT}. */
  protected visibleItems(row: OpenTable) {
    return row.id === this.selectedId() ? row.items : row.items.slice(0, COLLAPSED_ITEM_COUNT);
  }

  /**
   * The **quantity** hidden behind the collapse (not the number of hidden *lines*) — the handover counts articles,
   * so two folded lines of 3× and 2× read as "+ 5 weitere Artikel".
   *
   * @returns `0` when nothing is hidden, which the template's `@if` treats as absent.
   */
  protected hiddenQuantity(row: OpenTable): number {
    if (row.id === this.selectedId()) return 0;
    return row.items.slice(COLLAPSED_ITEM_COUNT).reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Scrolls a card into view **only if it is clipped** — the handover's §7.
   *
   * Measured with container-relative `getBoundingClientRect` deltas, deliberately **not** `offsetTop`: the card's
   * offset parent is the padded inner wrapper, not the scroll container, so `offsetTop` is short by the padding
   * (and by the whole inner offset in grid layout) and scrolls to the wrong place. A card that is already fully
   * visible is left completely alone — nudging the list under a user who can already see what they clicked is
   * the exact behaviour the rule exists to prevent.
   */
  revealCard(id: string): void {
    const container = this.scroller().nativeElement;
    const card = Array.from(container.querySelectorAll<HTMLElement>('[data-card-id]'))
      .find(element => element.dataset['cardId'] === id);
    if (!card) return;

    const cardBox = card.getBoundingClientRect();
    const containerBox = container.getBoundingClientRect();

    if (cardBox.top < containerBox.top + REVEAL_PADDING_PX) {
      container.scrollTo({
        top: container.scrollTop + (cardBox.top - containerBox.top) - REVEAL_PADDING_PX,
        behavior: 'smooth'
      });
    } else if (cardBox.bottom > containerBox.bottom - REVEAL_PADDING_PX) {
      container.scrollTo({
        top: container.scrollTop + (cardBox.bottom - containerBox.bottom) + REVEAL_PADDING_PX,
        behavior: 'smooth'
      });
    }
  }
}
